import { useEffect } from 'react'
import { useState } from 'react'
import { useRecoilState } from 'recoil'
import useRequest from '@/utils/useRequest'
import { videoAnnotationsState } from '@/store'
import { currentAnnotationState } from '@/store'
import { useIsRowView } from '@/store'
import { ocrState } from '@/store'
import AnnotationsPanel from './panel/AnnotationsPanel'
import Player from './Player'
import Editor from './Editor'
import Link from 'next/link'
import { useSession } from 'next-auth/client'
import AppSection from '@/components/app/AppSection'
import Logo from 'public/img/logos/logo.svg'
import { useRouter } from 'next/router'

export default function AnnotationsView({
  isReadOnly = false,
  video,
  videoError = null,
}) {
  const router = useRouter()
  const [session] = useSession()
  const videoID = video ? video._id : null
  const [annotations, setAnnotations] = useAnnotations(videoID)
  const [ocr] = useRecoilState(ocrState)
  const [isDragging, setIsDragging] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [, setCurrentAnnotation] = useRecoilState(currentAnnotationState)
  const queryAnnotationId = router.query.annotationId
  const isRowView = useIsRowView()

  useEffect(() => {
    if (queryAnnotationId && annotations) {
      const selectedAnnotation = annotations.find(
        ({ _id }) => _id === queryAnnotationId
      )

      if (selectedAnnotation) {
        setCurrentAnnotation(selectedAnnotation)
      }
    }
  }, [queryAnnotationId, annotations, setCurrentAnnotation])

  const videoNotFound = videoError && videoError.response.status === 404

  if (videoNotFound || !userHasAccessToVideo({ isReadOnly, session, video })) {
    return (
      <AppSection wrapContent={false}>
        <div className="flex center flex-col h-full">
          <h1 className="text-3xl mb-5 font-semibold">Video not found</h1>
          <Link href="/">
            <a className="center">
              <Logo className="w-10 h-10" />
              <span className="pl-3 text-2xl">Annotate.tv</span>
            </a>
          </Link>
        </div>
      </AppSection>
    )
  }

  const isLoading = !video || !annotations

  return (
    <AppSection wrapContent={false} isLoading={isLoading}>
      <>
        {ocr && (
          <div className="absolute top-0 left-0 right-0 bottom-0 bg-white z-40" />
        )}
        <div className="flex">
          <AnnotationsPanel
            {...{ setAnnotations, annotations, video, isReadOnly }}
          />

          <div
            className={`flex w-full overflow-auto h-screen ${
              isRowView ? 'flex-col' : 'flex-row'
            }`}
          >
            <Player
              {...{
                video,
                isDragging,
                setIsDragging,
                isHovering,
                setIsHovering,
              }}
            />

            <Editor
              {...{
                setAnnotations,
                video,
                isReadOnly,
                annotations,
                isDragging,
                isHovering,
              }}
            />
          </div>
        </div>
      </>
    </AppSection>
  )
}

function useAnnotations(videoID) {
  const { data: serverAnnotations } = useRequest(
    videoID ? `/api/annotations?videoId=${videoID}` : null
  )
  const [annotations, setAnnotations] = useRecoilState(videoAnnotationsState)

  useEffect(() => {
    setAnnotations(serverAnnotations)
  }, [serverAnnotations])

  return [annotations, setAnnotations]
}

function userHasAccessToVideo({ isReadOnly, session, video }) {
  if (isReadOnly || !session || !video) {
    return true
  }

  if (session && session.user._id === video.user) {
    return true
  } else {
    return false
  }
}
